import axios from 'axios';
import { useEffect, useMemo, useRef, useState, type ChangeEvent, type KeyboardEvent } from 'react';
import ReactMarkdown from 'react-markdown';
import { CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import remarkGfm from 'remark-gfm';
import useSWRMutation from 'swr/mutation';
import { Drawer, DrawerClose, DrawerContent, DrawerHeader, DrawerTitle } from '../../../../design-system/drawer/drawer';
import { Inline } from '../../../../design-system/inline/inline';
import { Input } from '../../../../design-system/input/input';
import { Stack } from '../../../../design-system/stack/stack';

type AskAiRequest = {
  query: string;
  conversationId?: string;
};

type AskAiResponse = {
  message: string; // plain text or markdown
  conversationId?: string;
};

type Message = {
  id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
};

type AskAiModalProps = {
  isOpen: boolean;
  initialQuery: string;
  onClose: () => void;
  /** For Storybook and local demos only: pre-populate messages without network */
  debugInitialMessages?: Array<Pick<Message, 'role' | 'content'>>;
};

export const AskAiDrawer = ({ isOpen, initialQuery, onClose, debugInitialMessages }: AskAiModalProps) => {
  const [conversationId, setConversationId] = useState<string | undefined>();
  const [input, setInput] = useState('');
  const [messages, setMessages] = useState<Message[]>([]);
  const messagesEndRef = useRef<HTMLDivElement | null>(null);

  const askAiFetcher = async <T, R>(url: string, { arg }: { arg: T }) => {
    const response = await axios.post<R>(url, arg, { timeout: 90000 });
    return response.data as R;
  };

  const { trigger: askAi, isMutating } = useSWRMutation<AskAiResponse, Error, string, AskAiRequest>(
    '/api/ai/ask-ai',
    askAiFetcher,
  );

  const canSend = useMemo(() => input.trim().length > 0 && !isMutating, [input, isMutating]);

  useEffect(() => {
    if (!isOpen) return;
    // Reset state on open
    setConversationId(undefined);
    setMessages([]);
    setInput('');

    const run = async () => {
      if (debugInitialMessages && debugInitialMessages.length > 0) {
        setMessages(debugInitialMessages.map((m) => ({ id: crypto.randomUUID(), role: m.role, content: m.content })));
        return;
      }

      if (!initialQuery.trim()) return;
      setMessages((prev) => [...prev, { id: crypto.randomUUID(), role: 'user', content: initialQuery.trim() }]);

      try {
        const response = await askAi({ query: initialQuery.trim() });
        setConversationId(response.conversationId);
        setMessages((prev) => [...prev, { id: crypto.randomUUID(), role: 'assistant', content: response.message }]);
      } catch {
        setMessages((prev) => [
          ...prev,
          { id: crypto.randomUUID(), role: 'system', content: 'Sorry, something went wrong fetching the AI response.' },
        ]);
      }
    };

    void run();
  }, [isOpen, initialQuery, askAi, debugInitialMessages]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = async () => {
    const text = input.trim();
    if (!text) return;
    setInput('');
    setMessages((prev) => [...prev, { id: crypto.randomUUID(), role: 'user', content: text }]);
    try {
      const response = await askAi({ query: text, conversationId });
      if (response.conversationId) setConversationId(response.conversationId);
      setMessages((prev) => [...prev, { id: crypto.randomUUID(), role: 'assistant', content: response.message }]);
    } catch {
      setMessages((prev) => [
        ...prev,
        { id: crypto.randomUUID(), role: 'system', content: 'Sorry, something went wrong fetching the AI response.' },
      ]);
    }
  };

  const Bubble = ({ role, children }: { role: Message['role']; children: React.ReactNode }) => {
    const base = 'rounded-lg px-3 py-2 text-sm whitespace-pre-wrap break-words max-w-[720px]';
    const palette =
      role === 'assistant'
        ? 'bg-gray-50 border border-gray-200 text-gray-900'
        : role === 'user'
          ? 'bg-seapoint-yellow/20 border border-seapoint-yellow text-gray-900'
          : 'bg-red-50 border border-red-200 text-red-800';
    return <div className={`${base} ${palette}`}>{children}</div>;
  };

  type ChartSpec = {
    type: 'line' | 'bar' | 'pie';
    title?: string;
    x: string[];
    series: { name: string; data: number[] }[];
    yFormat?: string; // e.g. currency:GBP, number, percent
  };

  const extractChartSpec = (text: string): ChartSpec | null => {
    const match = text.match(/```(?:chart-json|json)[\s\S]*?```/);
    if (!match) return null;
    const block = match[0];
    const json = block.replace(/^```chart-json\n?/, '').replace(/\n?```$/, '');
    try {
      const spec = JSON.parse(json) as ChartSpec;
      if (!spec || !Array.isArray(spec.x) || !Array.isArray(spec.series)) return null;
      return spec;
    } catch {
      return null;
    }
  };

  // Fallback: try to parse a simple 2-column Markdown table into a line chart
  const fallbackSpecFromMarkdownTable = (text: string): ChartSpec | null => {
    // Look for a table: header line with pipes, separator with dashes, then rows
    const lines = text.split(/\r?\n/);
    const startIdx = lines.findIndex((l) => /\|/.test(l) && /-+\s*\|\s*-+/.test(lines[lines.indexOf(l) + 1] || ''));
    if (startIdx === -1) return null;
    const rows = [] as Array<{ label: string; value: number }>;
    for (let i = startIdx + 2; i < lines.length; i++) {
      const row = lines[i].trim();
      if (!row.includes('|')) break;
      const cells = row
        .split('|')
        .map((c) => c.trim())
        .filter((c) => c.length > 0);
      if (cells.length < 2) continue;
      const label = cells[0];
      const num = Number(String(cells[1]).replace(/[^0-9.-]/g, ''));
      if (!Number.isFinite(num)) continue;
      rows.push({ label, value: num });
    }
    if (rows.length < 2) return null;
    return {
      type: 'line',
      title: undefined,
      x: rows.map((r) => r.label),
      series: [{ name: 'Value', data: rows.map((r) => r.value) }],
      yFormat: undefined,
    };
  };

  const formatY = (value: number, yFormat?: string) => {
    if (!yFormat) return value;
    if (yFormat.startsWith('currency:')) {
      const code = yFormat.split(':')[1] || 'GBP';
      try {
        return new Intl.NumberFormat(undefined, { style: 'currency', currency: code }).format(value);
      } catch {
        return value;
      }
    }
    if (yFormat === 'percent') return `${(value * 100).toFixed(1)}%`;
    return value;
  };

  return (
    <Drawer open={isOpen} onOpenChange={(open) => !open && onClose()} direction="right" modal={false}>
      <DrawerContent aria-describedby={undefined} className="flex h-full flex-col overflow-hidden">
        <DrawerHeader className="border-b border-gray-200 p-[18px]">
          <Inline align="center" justify="between">
            <DrawerTitle className="text-xl font-semibold tracking-wide">Ask AI 🤖</DrawerTitle>
            <DrawerClose />
          </Inline>
        </DrawerHeader>

        <div className="flex h-full min-h-0 flex-col">
          <div className="scrollbar-hide min-h-0 flex-1 overflow-y-auto p-[18px] [&::-webkit-scrollbar]:hidden">
            <Stack space={3}>
              {messages.map((m) => {
                const chartSpec =
                  m.role === 'assistant'
                    ? extractChartSpec(m.content) || fallbackSpecFromMarkdownTable(m.content)
                    : null;
                const contentWithoutChart =
                  m.role === 'assistant' && chartSpec
                    ? m.content.replace(/```chart-json[\s\S]*?```/, '').trim()
                    : m.content;

                return (
                  <div key={m.id} className={m.role === 'user' ? 'flex justify-end' : 'flex justify-start'}>
                    <Bubble role={m.role}>
                      {m.role === 'assistant' ? (
                        <div className="flex flex-col gap-3">
                          {chartSpec && chartSpec.type === 'line' && (
                            <div className="h-[260px] w-full">
                              <ResponsiveContainer width="100%" height="100%">
                                <LineChart
                                  data={chartSpec.x.map((x, i) => ({
                                    x,
                                    ...Object.fromEntries(chartSpec.series.map((s) => [s.name, s.data[i]])),
                                  }))}
                                >
                                  <CartesianGrid strokeDasharray="3 3" />
                                  <XAxis dataKey="x" />
                                  <YAxis tickFormatter={(v) => String(formatY(Number(v), chartSpec.yFormat))} />
                                  <Tooltip formatter={(v: unknown) => String(formatY(Number(v), chartSpec.yFormat))} />
                                  <Legend />
                                  {chartSpec.series.map((s, idx) => (
                                    <Line
                                      key={s.name}
                                      type="monotone"
                                      dataKey={s.name}
                                      stroke={['#2563eb', '#16a34a', '#dc2626', '#7c3aed'][idx % 4]}
                                      dot={false}
                                      strokeWidth={2}
                                    />
                                  ))}
                                </LineChart>
                              </ResponsiveContainer>
                            </div>
                          )}
                          {contentWithoutChart && (
                            <ReactMarkdown remarkPlugins={[remarkGfm]}>{contentWithoutChart}</ReactMarkdown>
                          )}
                        </div>
                      ) : (
                        <div>{m.content}</div>
                      )}
                    </Bubble>
                  </div>
                );
              })}
              {isMutating && (
                <div className="flex justify-start">
                  <Bubble role="assistant">
                    <div className="inline-flex items-center gap-2 text-gray-600">
                      <span className="h-2 w-2 animate-bounce rounded-full bg-gray-400 [animation-delay:-0.2s]" />
                      <span className="h-2 w-2 animate-bounce rounded-full bg-gray-400" />
                      <span className="h-2 w-2 animate-bounce rounded-full bg-gray-400 [animation-delay:0.2s]" />
                      <span className="ml-2">Thinking…</span>
                    </div>
                  </Bubble>
                </div>
              )}
              <div ref={messagesEndRef} />
            </Stack>
          </div>

          <div className="shrink-0 border-t border-gray-200 p-[18px]">
            <Input
              placeholder={isMutating ? 'Thinking…' : 'Type a follow-up and press Enter'}
              value={input}
              onChange={(e: ChangeEvent<HTMLInputElement>) => setInput(e.target.value)}
              onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => {
                if (e.key === 'Enter' && canSend) {
                  e.preventDefault();
                  void handleSend();
                }
              }}
              className="w-full"
            />
          </div>
        </div>
      </DrawerContent>
    </Drawer>
  );
};
